import type { CashPlanChoice } from '../lib/plan-catalog';

export type CashPlanSelection = { packageId: string; interval: 'month' | 'year'; priceMinor: number };

const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');

/**
 * The plan a cash entry starts a subscription on, for a business with NO live subscription.
 *
 * Why this exists (2026-09-15): "Record payment" on such a business used to invent a plan —
 * a package-less subscription at whatever amount was typed, billed monthly. Saudi Borka Fashion
 * paid ৳3,000 for Basic (৳3,000/year) and was booked as ৳3,000/month, due again in 30 days.
 * The API now refuses the entry unless the plan is named; this is where it gets named.
 *
 * Shows only the intervals a plan actually sells; picking a plan reports its price so the
 * caller can prefill the amount (the API requires the full price to start a plan).
 */
export function CashPlanPicker({
  plans,
  value,
  onChange,
  catalogLabel,
}: {
  plans: readonly CashPlanChoice[];
  value: { packageId: string; interval: 'month' | 'year' } | null;
  onChange: (next: CashPlanSelection | null) => void;
  /** e.g. "Ogrogoti" / "Possignin" — the catalog these plans come from. */
  catalogLabel?: string;
}) {
  const selected = value ? plans.find((p) => p.id === value.packageId) ?? null : null;
  const priceOf = (p: CashPlanChoice, iv: 'month' | 'year') => (iv === 'year' ? p.yearlyMinor : p.monthlyMinor);
  const firstInterval = (p: CashPlanChoice): 'month' | 'year' => (p.yearlyMinor !== null && p.monthlyMinor === null ? 'year' : p.monthlyMinor !== null ? 'month' : 'year');

  function pickPlan(p: CashPlanChoice) {
    const iv = value && priceOf(p, value.interval) !== null ? value.interval : firstInterval(p);
    onChange({ packageId: p.id, interval: iv, priceMinor: priceOf(p, iv)! });
  }
  function pickInterval(iv: 'month' | 'year') {
    if (!selected) return;
    const price = priceOf(selected, iv);
    if (price === null) return;
    onChange({ packageId: selected.id, interval: iv, priceMinor: price });
  }

  return (
    <div className="space-y-3">
      <div>
        <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">Plan the customer paid for</span>
        <span className="block text-xs text-zinc-500 dark:text-zinc-400 mb-2">
          This business has no active subscription — this payment starts one on the plan you pick.
          {catalogLabel ? <> Only {catalogLabel}&apos;s own packages are shown.</> : null}
        </span>
        {plans.length === 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-300">
            No published package with an active price is available for this business&apos;s catalog — publish one first.
          </p>
        ) : (
          <div className="space-y-2" role="radiogroup" aria-label="Plan">
            {plans.map((p) => {
              const isSel = selected?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={isSel}
                  onClick={() => pickPlan(p)}
                  className={`w-full min-h-11 flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border text-left transition active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                    isSel
                      ? 'border-brand bg-brand-subtle dark:bg-brand/15 dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-zinc-950 dark:text-zinc-50">{p.name}</span>
                    {p.caption && <span className="block text-xs text-zinc-500 dark:text-zinc-400 truncate">{p.caption}</span>}
                  </span>
                  <span className="text-xs tabular-nums text-zinc-600 dark:text-zinc-300 flex-shrink-0 text-right">
                    {p.monthlyMinor !== null && <span className="block">{taka(p.monthlyMinor)}<span className="text-zinc-500 dark:text-zinc-400">/month</span></span>}
                    {p.yearlyMinor !== null && <span className="block">{taka(p.yearlyMinor)}<span className="text-zinc-500 dark:text-zinc-400">/year</span></span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {selected && (
        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Billing interval</span>
          <div className="flex gap-2" role="radiogroup" aria-label="Billing interval">
            {(['month', 'year'] as const)
              .filter((iv) => priceOf(selected, iv) !== null)
              .map((iv) => (
                <button
                  key={iv}
                  type="button"
                  role="radio"
                  aria-checked={value?.interval === iv}
                  onClick={() => pickInterval(iv)}
                  className={`min-h-11 px-4 py-2 rounded-md text-sm border transition active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                    value?.interval === iv
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {iv === 'month' ? 'Monthly' : 'Yearly'} · {taka(priceOf(selected, iv)!)}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
